import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { ArrowRight, ArrowLeft, Brain, Check, Sparkles, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import type { SpaceSocket } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { integrationRegistry } from '../../integrations/registry/index';
import { IntegrationIcon } from '../integrations/shared/IntegrationIcon';
import { getAxiosErrorMessage, getMemaraApiErrorCode } from '../../utils/errorUtils';
import { isTimeoutOrNetworkError, pickSpaceToAdvance } from './spaceCreateRecovery';
import { getOrCreateDefaultSocket, isSocketUuid } from '../../utils/socketUtils';
import { spacesCache } from '../../hooks/useSpacesQuery';

/** Drop a stale empty list and load the space just created or recovered. */
async function refreshSpacesCacheAfterCreate(): Promise<void> {
  spacesCache.invalidate();
  try {
    await spacesCache.fetch(true);
  } catch (refreshError) {
    console.warn('Spaces cache refresh after onboarding create failed', refreshError);
  }
}

interface OnboardingFlowProps {
  onComplete: () => void;
}

interface OnboardingState {
  name: string;
  companySize: string;
  spaceName: string;
  spaceDescription: string;
  spaceIcon: string;
  createdSpaceId?: string;
  recommendedIntegrations: Array<{
    integration_id: string;
    relevance_score: number;
    reason: string;
  }>;
  currentIntegrationIndex: number;
  pendingIntegrations: string[];
  completedIntegrations: string[];
  createdBinding?: {
    bindingId: string;
    integrationId: string;
    integrationName: string;
    socketId: string;
    socketName: string;
  };
}

// Step 1: Name Collection
function NameStep({
  state,
  setState,
  onNext,
}: {
  state: OnboardingState;
  setState: React.Dispatch<React.SetStateAction<OnboardingState>>;
  onNext: () => void;
}) {
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (user && !state.name) {
      const name =
        user.user_metadata?.full_name ||
        user.user_metadata?.name ||
        user.email?.split('@')[0] ||
        '';
      if (name) {
        setState((prev) => ({ ...prev, name }));
      }
    }
  }, [user, state.name, setState]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col items-center justify-center h-full max-w-md mx-auto px-4"
    >
      <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
        <Brain className="w-8 h-8 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
        {t('improvedOnboarding.nameStep.title', { ns: 'onboarding', defaultValue: "What's your name?" })}
      </h2>
      <p className="text-gray-600 mb-6 text-center">
        {t('improvedOnboarding.nameStep.description', { ns: 'onboarding', defaultValue: 'We\'ll use this to personalize your experience' })}
      </p>
      <label htmlFor="onboarding-name" className="sr-only">
        {t('step1.fullName', { ns: 'onboarding', defaultValue: 'Full Name' })}
      </label>
      <Input
        id="onboarding-name"
        data-testid="onboarding-name-input"
        value={state.name}
        onChange={(e) => setState((prev) => ({ ...prev, name: e.target.value }))}
        placeholder={t('step1.fullNamePlaceholder', { ns: 'onboarding' }) as string}
        className="w-full mb-6"
        onKeyPress={(e) => {
          if (e.key === 'Enter' && state.name.trim()) {
            onNext();
          }
        }}
      />
      <Button
        onClick={onNext}
        disabled={!state.name.trim()}
        size="lg"
        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
      >
        Continue
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </motion.div>
  );
}

// Step 2: Company Size
function CompanySizeStep({
  state,
  setState,
  onNext,
}: {
  state: OnboardingState;
  setState: React.Dispatch<React.SetStateAction<OnboardingState>>;
  onNext: () => void;
}) {
  const { t } = useTranslation();

  const companySizes = [
    { id: 'individual', label: t('improvedOnboarding.companySize.individual', { ns: 'onboarding', defaultValue: 'Individual' }) },
    { id: '2-10', label: t('improvedOnboarding.companySize.small', { ns: 'onboarding', defaultValue: '2-10 employees' }) },
    { id: '11-50', label: t('improvedOnboarding.companySize.medium', { ns: 'onboarding', defaultValue: '11-50 employees' }) },
    { id: '51-200', label: t('improvedOnboarding.companySize.large', { ns: 'onboarding', defaultValue: '51-200 employees' }) },
    { id: '201-1000', label: t('improvedOnboarding.companySize.enterprise', { ns: 'onboarding', defaultValue: '201-1000 employees' }) },
    { id: '1000+', label: t('improvedOnboarding.companySize.enterprisePlus', { ns: 'onboarding', defaultValue: '1000+ employees' }) },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto px-4"
    >
      <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
        {t('improvedOnboarding.companySizeStep.title', { ns: 'onboarding', defaultValue: 'Are you working individually or with a team?' })}
      </h2>
      <p className="text-gray-600 mb-8 text-center">
        {t('improvedOnboarding.companySizeStep.description', { ns: 'onboarding', defaultValue: 'This helps us recommend the right integrations for you' })}
      </p>
      <div className="grid grid-cols-2 gap-4 w-full">
        {companySizes.map((size) => (
          <button
            key={size.id}
            onClick={() => setState((prev) => ({ ...prev, companySize: size.id }))}
            className={`p-4 rounded-lg border-2 transition-all ${
              state.companySize === size.id
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <span className="font-medium text-gray-900">{size.label}</span>
          </button>
        ))}
      </div>
      <Button
        onClick={onNext}
        disabled={!state.companySize}
        size="lg"
        className="w-full mt-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
      >
        Continue
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </motion.div>
  );
}

// Step 3: Memory Space Education (Optional)
function MemorySpaceEducationStep({
  onNext,
  onSkip,
}: {
  onNext: () => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto px-4"
    >
      <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6">
        <Sparkles className="w-8 h-8 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
        {t('improvedOnboarding.spaceEducationStep.title', { ns: 'onboarding', defaultValue: 'What are Memory Spaces?' })}
      </h2>
      <Card className="w-full mb-6">
        <CardContent className="p-6">
          <p className="text-gray-700 mb-4">
            {t('improvedOnboarding.spaceEducationStep.description', { ns: 'onboarding', defaultValue: 'Memory Spaces are isolated containers for your memories. Each space has its own integrations and can be completely separate from others.' })}
          </p>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start">
              <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>{t('improvedOnboarding.spaceEducationStep.benefit1', { ns: 'onboarding', defaultValue: 'Isolated memories with their own integrations' })}</span>
            </li>
            <li className="flex items-start">
              <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>{t('improvedOnboarding.spaceEducationStep.benefit2', { ns: 'onboarding', defaultValue: 'Complete data separation between spaces' })}</span>
            </li>
            <li className="flex items-start">
              <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>{t('improvedOnboarding.spaceEducationStep.benefit3', { ns: 'onboarding', defaultValue: 'Organize by project, team, or purpose' })}</span>
            </li>
          </ul>
        </CardContent>
      </Card>
      <div className="flex gap-4 w-full">
        <Button
          onClick={onSkip}
          variant="outline"
          size="lg"
          className="flex-1"
        >
          Skip
        </Button>
        <Button
          onClick={onNext}
          size="lg"
          className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
        >
          Continue
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </motion.div>
  );
}

// Step 4: First Space Creation
function SpaceCreationStep({
  state,
  setState,
  onNext,
  onBack,
  isLoading: _isLoading,
}: {
  state: OnboardingState;
  setState: React.Dispatch<React.SetStateAction<OnboardingState>>;
  onNext: () => void;
  onBack: () => void;
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const commonIcons = ['🧠', '💼', '🔧', '📚', '🎯', '🚀', '⭐', '💡'];

  const handleCreateSpace = async () => {
    if (!state.spaceName.trim() || isCreating) return;

    setIsCreating(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication required');
      apiService.setAuthToken(token);

      // First, check if user already has spaces (with timeout handling)
      try {
        const existingSpaces = await Promise.race([
          apiService.getAllSpaces(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout checking existing spaces')), 15000)
          )
        ]) as any;
        
        if (existingSpaces?.spaces && existingSpaces.spaces.length > 0) {
          // User already has spaces, use the first one
          console.log('User already has spaces, using existing space:', existingSpaces.spaces[0].space_id);
          setState((prev) => ({ ...prev, createdSpaceId: existingSpaces.spaces[0].space_id }));
          await refreshSpacesCacheAfterCreate();
          onNext();
          return;
        }
      } catch (checkError: any) {
        // Timeout or other error - proceed with creation
        if (checkError?.message?.includes('Timeout') || checkError?.code === 'ECONNABORTED') {
          console.warn('Timeout checking existing spaces, proceeding with creation');
        } else {
          console.warn('Failed to check existing spaces, proceeding with creation:', checkError);
        }
      }

      // No existing spaces, create a new one (with extended timeout for slow API)
      const space = await Promise.race([
        apiService.createSpace({
          name: state.spaceName.trim(),
          icon: state.spaceIcon || '🧠',
          color: '#6366F1',
          template_type: 'custom',
          privacy_level: 'personal',
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Space creation timeout - please try again')), 45000)
        )
      ]) as any;

      setState((prev) => ({ ...prev, createdSpaceId: space.space_id }));
      await refreshSpacesCacheAfterCreate();
      onNext();
    } catch (error: any) {
      console.error('Failed to create space:', error);

      const timedOutOrOffline = isTimeoutOrNetworkError(error);
      const isConflict = error?.response?.status === 409;

      if (timedOutOrOffline || isConflict) {
        try {
          const existingSpaces = await apiService.getAllSpaces();
          const recovered = pickSpaceToAdvance(existingSpaces?.spaces, state.spaceName);
          if (recovered?.space_id) {
            console.log('Space create recovered from existing spaces', recovered.space_id);
            setState((prev) => ({ ...prev, createdSpaceId: recovered.space_id }));
            await refreshSpacesCacheAfterCreate();
            onNext();
            return;
          }
        } catch (fallbackError) {
          console.error('Failed to get existing spaces as fallback:', fallbackError);
        }
      }

      let errorMessage = 'Failed to create space';

      if (timedOutOrOffline) {
        errorMessage =
          'The request took too long. The space may have been created - please refresh the page or try again.';
      } else {
        errorMessage = getAxiosErrorMessage(error, errorMessage);
        const apiCode = getMemaraApiErrorCode(error);
        if (apiCode === 'SPACE_LIMIT_EXCEEDED') {
          const errPayload = error?.response?.data?.error;
          const msg =
            typeof errPayload === 'object' &&
            errPayload &&
            typeof (errPayload as { message?: string }).message === 'string'
              ? (errPayload as { message: string }).message
              : null;
          errorMessage =
            msg ||
            'You have reached your space limit. Please upgrade your plan to create more spaces.';
        }
      }

      setError(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto px-4"
    >
      <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
        {t('improvedOnboarding.spaceCreationStep.title', { ns: 'onboarding', defaultValue: 'Create Your First Memory Space' })}
      </h2>
      <Card className="w-full mb-6">
        <CardContent className="p-6 space-y-4">
          <div>
            <label htmlFor="onboarding-space-name" className="block text-sm font-medium text-gray-700 mb-2">
              {t('improvedOnboarding.spaceCreationStep.nameLabel', { ns: 'onboarding', defaultValue: 'Space Name' })}
            </label>
            <Input
              id="onboarding-space-name"
              data-testid="onboarding-space-name-input"
              value={state.spaceName}
              onChange={(e) => setState((prev) => ({ ...prev, spaceName: e.target.value }))}
              placeholder={t('improvedOnboarding.spaceCreationStep.namePlaceholder', { ns: 'onboarding', defaultValue: 'e.g., Work Projects' }) as string}
            />
          </div>
          <div>
            <label htmlFor="onboarding-space-description" className="block text-sm font-medium text-gray-700 mb-2">
              {t('improvedOnboarding.spaceCreationStep.descriptionLabel', { ns: 'onboarding', defaultValue: 'Description (optional)' })}
            </label>
            <Textarea
              id="onboarding-space-description"
              data-testid="onboarding-space-description-input"
              value={state.spaceDescription}
              onChange={(e) => setState((prev) => ({ ...prev, spaceDescription: e.target.value }))}
              placeholder={t('improvedOnboarding.spaceCreationStep.descriptionPlaceholder', { ns: 'onboarding', defaultValue: 'What is this space for?' }) as string}
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('improvedOnboarding.spaceCreationStep.iconLabel', { ns: 'onboarding', defaultValue: 'Icon' })}
            </label>
            <div className="flex gap-2 flex-wrap">
              {commonIcons.map((icon) => (
                <button
                  key={icon}
                  onClick={() => setState((prev) => ({ ...prev, spaceIcon: icon }))}
                  className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center text-xl transition-all ${
                    state.spaceIcon === icon
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
      {/* Error Display */}
      {error && (
        <div
          className="w-full mb-4 p-4 bg-red-50 border border-red-200 rounded-lg"
          data-testid="onboarding-space-create-error"
          role="alert"
        >
          <div className="flex items-start">
            <X className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Failed to create space</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600 ml-2"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-4 w-full">
        <Button 
          onClick={onBack} 
          variant="outline" 
          size="lg" 
          className="flex-1" 
          disabled={isCreating}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          data-testid="onboarding-create-space-btn"
          onClick={handleCreateSpace}
          disabled={!state.spaceName.trim() || isCreating}
          size="lg"
          className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCreating ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Creating...
            </>
          ) : (
            <>
              {t('improvedOnboarding.spaceCreationStep.createButton', { ns: 'onboarding', defaultValue: 'Create Space' })}
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
}

// Step 5: Integration Recommendation
function IntegrationRecommendationStep({
  state,
  setState,
  onNext,
  onBack,
  isLoading,
  onIntegrationCreated,
}: {
  state: OnboardingState;
  setState: React.Dispatch<React.SetStateAction<OnboardingState>>;
  onNext: () => void;
  onBack: () => void;
  isLoading: boolean;
  onIntegrationCreated?: () => void;
}) {
  const { t } = useTranslation();
  const [isConnecting, setIsConnecting] = useState(false);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [resolvedSocket, setResolvedSocket] = useState<SpaceSocket | null>(null);

  const currentIntegration = state.recommendedIntegrations[state.currentIntegrationIndex];
  const integrationDef = currentIntegration
    ? integrationRegistry[currentIntegration.integration_id]
    : null;

  const handleConnect = async () => {
    if (!state.createdSpaceId || !integrationDef) return;
    setIsConnecting(true);
    try {
      const socket = await getOrCreateDefaultSocket(state.createdSpaceId, integrationDef);
      if (!isSocketUuid(socket.socket_id)) {
        console.error('Resolved socket is missing a UUID', socket.socket_id);
        return;
      }
      setResolvedSocket(socket);
      setShowIntegrationModal(true);
    } catch (error) {
      console.error('Failed to resolve a socket before opening the integration:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleSkip = () => {
    if (state.currentIntegrationIndex < state.recommendedIntegrations.length - 1) {
      setState((prev) => ({
        ...prev,
        currentIntegrationIndex: prev.currentIntegrationIndex + 1,
      }));
    } else {
      // No more integrations, allow completion
      onNext();
    }
  };

  if (!currentIntegration || !integrationDef) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col items-center justify-center h-full"
      >
        <p className="text-gray-600 mb-4">No more integrations to show</p>
        <Button onClick={onNext} size="lg">
          Continue to Dashboard
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto px-4"
    >
      <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
        {t('improvedOnboarding.integrationStep.title', { ns: 'onboarding', defaultValue: 'Connect Your First Integration' })}
      </h2>
      <p className="text-gray-600 mb-6 text-center">
        {t('improvedOnboarding.integrationStep.description', { ns: 'onboarding', defaultValue: 'We recommend starting with this integration based on your profile' })}
      </p>
      <Card className="w-full mb-6">
        <CardContent className="p-6">
          <div className="flex items-center mb-4">
            <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${integrationDef.gradient} flex items-center justify-center mr-4`}>
              <IntegrationIcon icon={integrationDef.icon} className="w-6 h-6 text-white" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">{integrationDef.name}</h3>
              <p className="text-sm text-gray-600">{integrationDef.description}</p>
            </div>
          </div>
          {currentIntegration.reason && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-700">{currentIntegration.reason}</p>
            </div>
          )}
        </CardContent>
      </Card>
      <div className="flex gap-4 w-full">
        <Button onClick={onBack} variant="outline" size="lg" className="flex-1" disabled={isLoading || isConnecting}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleSkip}
          variant="outline"
          size="lg"
          className="flex-1"
          disabled={isLoading || isConnecting}
        >
          Skip
        </Button>
        <Button
          onClick={handleConnect}
          disabled={isLoading || isConnecting}
          size="lg"
          className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
        >
          Connect
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
      {showIntegrationModal && resolvedSocket && integrationDef.components?.modal && (
        <integrationDef.components.modal
          isOpen={showIntegrationModal}
          onClose={() => {
            console.log('🔒 Integration modal closing, createdBinding:', state.createdBinding);
            setShowIntegrationModal(false);
            // If integration was created, transition to setup step
            // Use a small delay to ensure state is updated from onCreateIntegration
            setTimeout(() => {
              if (state.createdBinding) {
                console.log('✅ Integration created, transitioning to setup step');
                onIntegrationCreated?.();
                onNext();
              }
            }, 200);
          }}
          onCreateIntegration={async (data: any) => {
            setIsConnecting(true);
            try {
              // Get default socket for this integration
              const sockets = await apiService.getSocketsForSpace(state.createdSpaceId!);
              
              // Find default socket matching integration's socket types
              // First try to find by template_id and "default" in name
              let defaultSocket = sockets.sockets.find((s) => {
                const socketType = s.socket_config?.template_id;
                const isDefault = s.socket_name.toLowerCase().includes('default');
                const isCompatible = integrationDef.socketTypes.includes(socketType as any);
                return isCompatible && isDefault;
              });
              
              // Fallback: if no default socket found, try to find any compatible socket
              if (!defaultSocket) {
                defaultSocket = sockets.sockets.find((s) => {
                  const socketType = s.socket_config?.template_id;
                  return integrationDef.socketTypes.includes(socketType as any);
                });
              }
              
              // Fallback: try to infer socket type from name if template_id is missing
              if (!defaultSocket) {
                defaultSocket = sockets.sockets.find((s) => {
                  const socketName = s.socket_name.toLowerCase();
                  return integrationDef.socketTypes.some((type) => {
                    if (type === 'api' && socketName.includes('api')) return true;
                    if (type === 'mcp' && socketName.includes('mcp')) return true;
                    if (type === 'webhooks' && socketName.includes('webhook')) return true;
                    if (type === 'events' && socketName.includes('event')) return true;
                    if (type === 'bots' && socketName.includes('bot')) return true;
                    return false;
                  });
                });
              }
              
              if (!defaultSocket) {
                console.error('Available sockets:', sockets.sockets.map(s => ({
                  name: s.socket_name,
                  template_id: s.socket_config?.template_id,
                  config: s.socket_config
                })));
                console.error('Integration socket types:', integrationDef.socketTypes);
                throw new Error(`No compatible socket found for integration type ${integrationDef.id}. Required socket types: ${integrationDef.socketTypes.join(', ')}`);
              }

              const binding = await apiService.createIntegrationBinding(defaultSocket.socket_id, data);
              
              if (!binding || !binding.binding_id) {
                throw new Error('Failed to create integration binding: invalid response');
              }
              
              // Add space_id to binding for modal compatibility
              const bindingWithSpace = {
                ...binding,
                space_id: state.createdSpaceId || defaultSocket.space_id,
              };
              
              // Add to pending integrations and store binding info
              setState((prev) => ({
                ...prev,
                pendingIntegrations: [...prev.pendingIntegrations, integrationDef.id],
                createdBinding: {
                  bindingId: binding.binding_id,
                  integrationId: integrationDef.id,
                  integrationName: binding.integration_name,
                  socketId: defaultSocket.socket_id,
                  socketName: defaultSocket.socket_name,
                },
              }));
              
              console.log('✅ Integration created successfully:', {
                bindingId: binding.binding_id,
                integrationId: integrationDef.id,
                socketId: defaultSocket.socket_id,
              });
              
              // Return binding with space_id for modal to use
              return bindingWithSpace;
            } catch (error) {
              console.error('Failed to create integration:', error);
              // Re-throw error so modal can handle it
              throw error;
            } finally {
              setIsConnecting(false);
            }
          }}
          spaceId={state.createdSpaceId ?? ''}
          socket={resolvedSocket}
        />
      )}
    </motion.div>
  );
}

// Step 6: Integration Setup (Waiting for Connection)
function IntegrationSetupStep({
  state,
  setState,
  onNext,
  onBack,
  onSkip,
  onComplete,
}: {
  state: OnboardingState;
  setState: React.Dispatch<React.SetStateAction<OnboardingState>>;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  onComplete: () => void;
}) {
  const { getAccessToken } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [, setBindingConfig] = useState<any>(null);
  const [integrationData, setIntegrationData] = useState<any>(null);

  const binding = state.createdBinding;
  const integrationDef = binding
    ? integrationRegistry[binding.integrationId]
    : null;

  // Load binding config when details modal is opened
  useEffect(() => {
    if (!showDetailsModal || !binding) return;

    const loadConfig = async () => {
      try {
        const token = await getAccessToken();
        if (!token) return;
        apiService.setAuthToken(token);
        
        // Get binding from socket first (this is more reliable)
        const bindings = await apiService.getIntegrationBindings(binding.socketId);
        const bindingRecord = bindings.bindings.find((b) => b.binding_id === binding.bindingId);
        
        if (bindingRecord) {
          // Try to get config, but don't fail if it errors
          let config: any = null;
          try {
            config = await apiService.getIntegrationConfig(binding.bindingId);
            setBindingConfig(config);
          } catch (configError) {
            console.warn('Failed to load config, using binding data:', configError);
            setBindingConfig({ error: true });
          }
          
          // Format integration data for success modal
          // Use config if available, otherwise use binding data
          const integrationDataForModal = config && !(config as any).error ? {
            integration_name: bindingRecord.integration_name,
            binding_id: bindingRecord.binding_id,
            auth_config: config.auth_config || bindingRecord.auth_config || {},
            permissions: config.permissions || bindingRecord.permissions || {},
            // Add any additional fields from config
            ...(config.oauth_config && { oauth_config: config.oauth_config }),
          } : {
            integration_name: bindingRecord.integration_name,
            binding_id: bindingRecord.binding_id,
            auth_config: bindingRecord.auth_config || {},
            permissions: bindingRecord.permissions || {},
          };
          
          setIntegrationData(integrationDataForModal);
        } else {
          // Fallback: use basic data from state
          setIntegrationData({
            integration_name: binding.integrationName,
            binding_id: binding.bindingId,
            auth_config: {},
            permissions: {},
          });
        }
      } catch (error) {
        console.error('Failed to load binding data:', error);
        setBindingConfig({ error: true });
        // Still set basic integration data so modal can show
        setIntegrationData({
          integration_name: binding.integrationName,
          binding_id: binding.bindingId,
          auth_config: {},
          permissions: {},
        });
      }
    };

    loadConfig();
  }, [showDetailsModal, state.createdBinding, getAccessToken]);

  // Poll for activation status and auto-complete when active
  useEffect(() => {
    if (!binding || isActive) return;

    const pollStatus = async () => {
      try {
        const token = await getAccessToken();
        if (!token) return;
        apiService.setAuthToken(token);

        const bindings = await apiService.getIntegrationBindings(binding.socketId);
        const bindingRecord = bindings.bindings.find((b) => b.binding_id === binding.bindingId);
        
        if (bindingRecord && bindingRecord.status === 'active') {
          console.log('✅ Integration activated! Auto-completing onboarding...');
          setIsActive(true);
          setState((prev) => ({
            ...prev,
            completedIntegrations: [...prev.completedIntegrations, binding.integrationId],
          }));
          
          // Auto-complete onboarding after a brief delay
          setTimeout(() => {
            onComplete();
          }, 1500);
        }
      } catch (error) {
        console.error('Failed to poll binding status:', error);
      }
    };

    // Poll every 3 seconds
    const interval = setInterval(pollStatus, 3000);
    pollStatus(); // Initial check

    return () => clearInterval(interval);
  }, [binding, isActive, getAccessToken, setState, onComplete]);

  if (!binding || !integrationDef) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col items-center justify-center h-full"
      >
        <p className="text-gray-600 mb-4">No integration to set up</p>
        <Button onClick={onNext} size="lg">
          Continue
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto px-4"
    >
      <div className="w-full space-y-6">
        {/* Header with Integration Info */}
        <div className="text-center">
          <div className={`w-20 h-20 bg-gradient-to-br ${integrationDef.gradient} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
            <IntegrationIcon icon={integrationDef.icon} className="w-10 h-10 text-white" size={40} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {integrationDef.name} Integration
          </h2>
          <p className="text-gray-600 mb-4">
            {binding.integrationName}
          </p>
        </div>

        {/* Waiting Animation */}
        <Card className="border-2 border-yellow-200 bg-yellow-50">
          <CardContent className="p-8 text-center">
            <div className="flex flex-col items-center space-y-4">
              {/* Animated Pulsing Circle */}
              <div className="relative">
                <div className="w-16 h-16 border-4 border-yellow-400 rounded-full animate-pulse"></div>
                <div className="absolute inset-0 w-16 h-16 border-4 border-yellow-300 rounded-full animate-ping opacity-75"></div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                  Waiting for Connection...
                </h3>
                <p className="text-sm text-yellow-800">
                  We're waiting for your first memory or event from this integration.
                </p>
                <p className="text-xs text-yellow-700 mt-2">
                  Once connected, onboarding will complete automatically.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Integration Success Modal (when View Details is clicked) */}
        {showDetailsModal && integrationDef.components?.successModal && integrationData && (
          <integrationDef.components.successModal
            isOpen={showDetailsModal}
            onClose={() => setShowDetailsModal(false)}
            integration={integrationData}
            spaceName={binding.socketName}
          />
        )}

        {/* Success Message (when active) */}
        {isActive && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-6 text-center">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <Check className="w-6 h-6 text-green-600" />
                  <h3 className="text-lg font-semibold text-green-900">
                    Connection Established!
                  </h3>
                </div>
                <p className="text-sm text-green-800">
                  Your integration is now active. Completing onboarding...
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Actions */}
        <div className="flex gap-4">
          <Button 
            onClick={onBack} 
            variant="outline" 
            size="lg" 
            className="flex-1"
            disabled={isActive}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={() => setShowDetailsModal(true)}
            variant="outline"
            size="lg"
            className="flex-1"
            disabled={isActive}
          >
            View Setup Details
          </Button>
          <Button
            onClick={onSkip}
            variant="outline"
            size="lg"
            className="flex-1"
            disabled={isActive}
          >
            Skip
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

// Step 7: Completion
function CompletionStep({ onNext }: { onNext: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col items-center justify-center h-full max-w-md mx-auto px-4"
    >
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
        <Check className="w-8 h-8 text-green-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
        {t('improvedOnboarding.completionStep.title', { ns: 'onboarding', defaultValue: 'You\'re All Set!' })}
      </h2>
      <p className="text-gray-600 mb-8 text-center">
        {t('improvedOnboarding.completionStep.description', { ns: 'onboarding', defaultValue: 'Your memory space is ready. Start creating memories and connecting integrations.' })}
      </p>
      <Button
        data-testid="onboarding-go-to-dashboard-btn"
        onClick={() => {
          onNext(); // This calls onComplete() which clears onboarding state
          navigate('/dashboard'); // Navigate to dashboard after onboarding completion
        }}
        size="lg"
        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
      >
        {t('improvedOnboarding.completionStep.button', { ns: 'onboarding', defaultValue: 'Go to Dashboard' })}
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </motion.div>
  );
}

export default function ImprovedOnboardingFlow({ onComplete }: OnboardingFlowProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading] = useState(false);
  const [state, setState] = useState<OnboardingState>({
    name: '',
    companySize: '',
    spaceName: '',
    spaceDescription: '',
    spaceIcon: '🧠',
    recommendedIntegrations: [],
    currentIntegrationIndex: 0,
    pendingIntegrations: [],
    completedIntegrations: [],
  });
  const [pendingStepTransition, setPendingStepTransition] = useState(false);
  const { getAccessToken } = useAuth();

  // Define steps array
  const steps = [
    { component: NameStep, title: 'Name', optional: false },
    { component: CompanySizeStep, title: 'Company Size', optional: false },
    { component: MemorySpaceEducationStep, title: 'Space Education', optional: true },
    { component: SpaceCreationStep, title: 'Create Space', optional: false },
    { component: IntegrationRecommendationStep, title: 'Connect Integration', optional: false },
    { component: IntegrationSetupStep, title: 'Setup Integration', optional: true },
    { component: CompletionStep, title: 'Complete', optional: false },
  ];

  // Handle step transition after integration creation
  useEffect(() => {
    if (pendingStepTransition && state.createdBinding) {
      console.log('🚀 Executing pending step transition to setup step', {
        createdBinding: state.createdBinding,
        currentStep,
      });
      setPendingStepTransition(false);
      // Setup step is always at index 5 (after IntegrationRecommendationStep)
      const setupStepIndex = 5;
      console.log('📍 Setup step index:', setupStepIndex);
      if (setupStepIndex < steps.length) {
        console.log('✅ Moving to setup step');
        setCurrentStep(setupStepIndex);
      } else {
        console.error('❌ Setup step index out of bounds');
      }
    }
  }, [pendingStepTransition, state.createdBinding, currentStep]);

  // Load recommended integrations when company size is set
  useEffect(() => {
    if (state.companySize && state.recommendedIntegrations.length === 0) {
      loadRecommendedIntegrations();
    }
  }, [state.companySize]);

  const loadRecommendedIntegrations = async () => {
    try {
      const token = await getAccessToken();
      if (!token) {
        console.warn('No auth token available for loading recommendations');
        return;
      }
      apiService.setAuthToken(token);

      // Add timeout handling for slow API responses
      const recommendations = await Promise.race([
        apiService.getRecommendedIntegrations({
          company_size: state.companySize,
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout loading recommendations')), 70000)
        )
      ]) as any;

      console.log('Loaded recommended integrations:', recommendations);
      
      // Filter out integrations that don't exist in the registry
      const validRecommendations = recommendations.filter((rec: any) => {
        const exists = integrationRegistry[rec.integration_id];
        if (!exists) {
          console.warn(`Integration ${rec.integration_id} not found in registry`);
        }
        return exists;
      });
      
      console.log('Valid recommendations:', validRecommendations);
      
      setState((prev) => ({
        ...prev,
        recommendedIntegrations: validRecommendations,
      }));
    } catch (error: any) {
      // Handle timeout gracefully - user can still proceed without recommendations
      if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout') || error?.message?.includes('Timeout')) {
        console.warn('Timeout loading recommended integrations - proceeding without recommendations');
      } else {
        console.error('Failed to load recommended integrations:', error);
        console.error('Error details:', {
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });
      }
      // Set empty array on error so user can still proceed
      setState((prev) => ({
        ...prev,
        recommendedIntegrations: [],
      }));
    }
  };

  const handleNext = () => {
    console.log('🔄 handleNext called, currentStep:', currentStep, 'createdBinding:', state.createdBinding);
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      console.log('📋 Next step would be:', steps[nextStep].title, 'component:', steps[nextStep].component.name);
      // If we're on IntegrationRecommendationStep and a binding was created, go to setup step
      if (steps[currentStep].component === IntegrationRecommendationStep && state.createdBinding) {
        const setupStepIndex = steps.findIndex(s => s.component === IntegrationSetupStep);
        if (setupStepIndex !== -1) {
          console.log('✅ Moving to setup step (integration created)');
          setCurrentStep(setupStepIndex);
          return;
        }
      }
      // Skip IntegrationSetupStep if no binding was created
      if (steps[nextStep].component === IntegrationSetupStep && !state.createdBinding) {
        console.log('⏭️ Skipping setup step - no binding created');
        if (nextStep + 1 < steps.length) {
          setCurrentStep(nextStep + 1);
        } else {
          onComplete();
        }
      } else {
        console.log('✅ Moving to next step:', steps[nextStep].title);
        setCurrentStep(nextStep);
      }
    } else {
      console.log('🏁 Onboarding complete');
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    // Skip optional steps
    if (steps[currentStep].optional) {
      handleNext();
    }
  };

  const CurrentStepComponent = steps[currentStep].component;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Progress Indicator */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-2">
            {steps.map((_, index) => (
              <React.Fragment key={index}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    index <= currentStep
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  {index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`w-12 h-1 rounded transition-colors ${
                      index < currentStep ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content - Fixed height container */}
        <div className="h-[500px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            {CurrentStepComponent === NameStep && (
              <NameStep key="name" state={state} setState={setState} onNext={handleNext} />
            )}
            {CurrentStepComponent === CompanySizeStep && (
              <CompanySizeStep key="company" state={state} setState={setState} onNext={handleNext} />
            )}
            {CurrentStepComponent === MemorySpaceEducationStep && (
              <MemorySpaceEducationStep key="education" onNext={handleNext} onSkip={handleSkip} />
            )}
            {CurrentStepComponent === SpaceCreationStep && (
              <SpaceCreationStep
                key="space"
                state={state}
                setState={setState}
                onNext={handleNext}
                onBack={handleBack}
                isLoading={isLoading}
              />
            )}
            {CurrentStepComponent === IntegrationRecommendationStep && (
              <IntegrationRecommendationStep
                key="integration"
                state={state}
                setState={setState}
                onNext={handleNext}
                onBack={handleBack}
                isLoading={isLoading}
                onIntegrationCreated={() => {
                  console.log('🔄 Integration created callback triggered');
                }}
              />
            )}
            {CurrentStepComponent === IntegrationSetupStep && (
              <>
                {console.log('🎯 Rendering IntegrationSetupStep, createdBinding:', state.createdBinding)}
                <IntegrationSetupStep
                  key="setup"
                  state={state}
                  setState={setState}
                  onNext={handleNext}
                  onBack={handleBack}
                  onSkip={handleSkip}
                  onComplete={onComplete}
                />
              </>
            )}
            {CurrentStepComponent === CompletionStep && (
              <CompletionStep key="complete" onNext={handleNext} />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

